import React, { useEffect, useState } from "react";
import { Bot, ChevronDown, Edit2, HelpCircle, Plus, Save, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { agentInstanceService } from "../../../services/agentService";
import ConfirmationDialog from "../../ui/ConfirmationDialog";
import { getUser } from "../../../lib/storage";
import { tenantService } from "../../../services/tenantService";
import { useAuth } from "../../../context/AuthContext";
import { Button, Spinner } from '../../ui/Button';
import { TextArea, TextField } from '../../ui/Field';
import { Surface } from '../../ui/Surface';

interface QA {
    id: string;
    question: string;
    answer: string;
    open?: boolean;
    embeddingStatus?: string;
}

interface ConfirmationDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    loading?: boolean;
    variant?: 'error' | 'warning';
}


const QandATab: React.FC<{ agent?: any }> = ({ agent }) => {
    const [qas, setQas] = useState<QA[]>([]);
    const [isAdding, setIsAdding] = useState(false);
    const [question, setQuestion] = useState("");
    const [answer, setAnswer] = useState("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [search, setSearch] = useState('');
    const [tenantName, setTenantName] = useState<string>('');
    const { isAuthenticated, isLoading } = useAuth();

    const fetchTenantName = async () => {
        try {
            const user = getUser() as any;
            const embeddedTenantName = user?.tenant?.name || user?.user?.tenant?.name;
            if (embeddedTenantName) {
                setTenantName(String(embeddedTenantName));
                return;
            }

            const tenantId = user?.tenantId || user?.user?.tenantId || localStorage.getItem('agentic_tenant_id');
            if (!tenantId) return;

            const tenant = await tenantService.get(tenantId);
            const resolvedTenantName = (tenant as any)?.name || (tenant as any)?.tenantName || '';
            if (resolvedTenantName) setTenantName(String(resolvedTenantName));
        } catch (err) {
            console.error("Failed to load tenant name", err);
        }
    };

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            fetchTenantName();
        }
    }, [isAuthenticated, isLoading]);

    useEffect(() => {
        if (!agent?.id) {
            setQas([]);
            return;
        }
        const qaTimeout = setTimeout(() => {
            fetchQAs();
        }, 500)
        return () => clearTimeout(qaTimeout)
    }, [agent?.id, search]);

    async function fetchQAs() {
        if (!agent?.id) return;
        setLoading(true);
        try {
            const res = await agentInstanceService.listQA({ agentInstanceId: agent.id, search: search, limit: 999999 });

            if (res && Array.isArray((res as any).records)) {
                const records = (res as any).records.map((r: any) => ({ ...r, open: !!r.open }));
                setQas(records);
                return;
            }
            if (res && Array.isArray((res as any).items)) {
                setQas((res as any).items);
                return;
            }
            if (Array.isArray(res)) {
                setQas(res);
                return;
            }
            const possible = (res as any).data ?? (res as any).list ?? [];
            setQas(Array.isArray(possible) ? possible : []);
        } catch (err) {
            console.error("Failed to load QAs", err);
            toast.error("Failed to load Q&A. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    function resetForm() {
        setQuestion("");
        setAnswer("");
        setEditingId(null);
        setIsAdding(false);
    }

    const handleSubmit = async () => {
        if (!question.trim()) {
            toast.error("Question is required");
            return;
        }

        const payload = {
            question: question.trim(),
            answer: answer.trim(),
            agentInstanceId: agent?.id,
        };

        try {
            if (editingId) {
                const updated = await agentInstanceService.updateQA(editingId, payload);
                toast.success(updated?.message || "Updated successfully");
                fetchQAs();
            } else {
                const created = await agentInstanceService.createQA(payload);
                toast.success(created?.message || "Created successfully");
                fetchQAs();
            }
            resetForm();
        } catch (err: any) {
            toast.error("Failed to create/update Q&A");
        }
    };

    function toggleOpen(id: string) {
        setQas(prev => prev.map(q => (q.id === id ? { ...q, open: !q.open } : q)));
    }

    function onEdit(id: string) {
        const existing = qas.find(q => q.id === id);
        if (!existing) return;
        setQuestion(existing.question);
        setAnswer(existing.answer);
        setEditingId(id);
        setIsAdding(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    function confirmDelete(id: string) {
        setDeleteId(id);
        setConfirmOpen(true);
    }

    async function doDelete() {
        if (!deleteId) return;
        setDeleting(true);
        try {
            const res: any = await agentInstanceService.deleteQA(deleteId);
            toast.success(res?.message || "Deleted successfully");
            fetchQAs();
        } catch (err: any) {
            toast.error("Failed to delete Q&A");
        } finally {
            setDeleting(false);
            setConfirmOpen(false);
            setDeleteId(null);
        }
    }

    async function trainQA() {
        if (!agent?.id) return;
        try {
            const res: any = await agentInstanceService.trainQA({
                agentInstanceId: agent.id,
            });
            toast.success(res?.message || "Training initiated successfully");
        } catch (err: any) {
            toast.error("Failed to initiate training");
        }
    }

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            handleSubmit();
        }
    };

    return (
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 p-6">
            <ConfirmationDialog
                isOpen={confirmOpen}
                onClose={() => { setConfirmOpen(false); setDeleteId(null); }}
                onConfirm={doDelete}
                title="Delete Q&A"
                message="Are you sure you want to delete this Q&A? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
                loading={deleting}
                variant="error"
            />

            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="p-2 bg-blue-100 rounded-lg">
                                <HelpCircle className="w-6 h-6 text-blue-600" />
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900">Q&A Management</h1>
                        </div>
                        <Button tone="custom"
                            // onClick={() => setIsAdding(true)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-green-700 hover:to-emerald-700 transition-all duration-200 font-medium"
                            onClick={() => {
                                trainQA();
                            }}
                        >
                            <Bot className="w-5 h-5" />
                            Train
                        </Button>

                    </div>
                    <p className="text-gray-600 ml-14">Manage your agent's knowledge base with questions and answers</p>
                </div>

                {/* Action Bar */}
                <Surface tone="card-soft" className="rounded-xl border p-4 mb-6 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-gray-700">
                            Total Q&As: <span className="text-blue-600 font-semibold">{qas.length}</span>
                        </span>
                        <div className="w-full max-w-xs">
                            <TextField tone="default-focus-primary"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search documents..."
                                className="w-full px-3 py-2 rounded-lg border focus:ring-2"
                            />
                        </div>
                    </div>
                    <Button tone="custom"
                        onClick={() => setIsAdding(true)}
                        className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg shadow-md hover:shadow-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 font-medium"
                    >
                        <Plus className="w-5 h-5" />
                        Add New Q&A
                    </Button>
                </Surface>

                {/* Add/Edit Form */}
                {isAdding && (
                    <Surface tone="card-raised" className="rounded-xl border p-6 mb-6 animate-in">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-xl font-semibold text-gray-900">
                                {editingId ? 'Edit Q&A' : 'Add New Q&A'}
                            </h2>
                            <Button tone="custom"
                                onClick={resetForm}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                title="Close"
                            >
                                <X className="w-5 h-5 text-gray-500" />
                            </Button>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Question <span className="text-red-500">*</span>
                                </label>
                                <TextArea
                                    value={question}
                                    onChange={e => setQuestion(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    rows={3}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                    placeholder="Enter your question here..."
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Answer
                                </label>
                                <TextArea
                                    value={answer}
                                    onChange={e => setAnswer(e.target.value)}
                                    onKeyDown={handleKeyPress}
                                    rows={4}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none"
                                    placeholder="Enter the answer here..."
                                />
                                <p className="text-xs text-gray-500 mt-2">Press Ctrl+Enter (Cmd+Enter on Mac) to save</p>
                            </div>

                            <div className="space-y-1">
                                <label className="block text-sm font-medium text-agentic-text-primary mb-1">Tenant</label>
                                <div className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-800">
                                    {tenantName || 'Current tenant unavailable'}
                                </div>
                                <p className="text-xs text-gray-500">
                                    Q&A entries are stored against the active tenant. A client selection is not required.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                                <Button tone="custom"
                                    onClick={resetForm}
                                    className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                >
                                    Cancel
                                </Button>
                                <Button tone="custom"
                                    onClick={handleSubmit}
                                    className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium shadow-md hover:shadow-lg"
                                >
                                    <Save className="w-4 h-4" />
                                    {editingId ? 'Update Q&A' : 'Save Q&A'}
                                </Button>
                            </div>
                        </div>
                    </Surface>
                )}

                {/* Q&A List */}
                <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-4">
                    {loading && (
                        <Surface tone="card-soft" className="rounded-xl border p-12 text-center">
                            <Spinner as="div" className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></Spinner>
                            <p className="text-gray-600">Loading Q&As...</p>
                        </Surface>
                    )}

                    {(!loading && !isAdding && qas.length === 0) && (
                        <Surface tone="card-soft" className="rounded-xl border p-12 text-center">
                            <div className="p-4 bg-gray-100 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                <HelpCircle className="w-10 h-10 text-gray-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Q&As Yet</h3>
                            <p className="text-gray-600 mb-6">Get started by adding your first question and answer</p>
                            <Button tone="custom"
                                onClick={() => setIsAdding(true)}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all font-medium"
                            >
                                <Plus className="w-5 h-5" />
                                Add Your First Q&A
                            </Button>
                        </Surface>
                    )}

                    {qas.map((q, index) => (
                        <Surface tone="card-hover"
                            key={q.id}
                            className="rounded-xl border overflow-hidden transition-all duration-200"
                            style={{ animationDelay: `${index * 50}ms` }}
                        >
                            <div
                                className="p-5 flex items-center justify-between cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => toggleOpen(q.id)}
                            >
                                <div className="flex items-start gap-4 flex-1">
                                    <div className="p-2 bg-blue-100 rounded-lg mt-1">
                                        <HelpCircle className="w-5 h-5 text-blue-600" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-lg font-medium text-gray-900 leading-relaxed">{q.question}</h3>
                                    </div>

                                </div>
                                <div className={`text-sm text-gray-500 mr-4 ${q.embeddingStatus === 'completed' ? 'text-green-600' : q.embeddingStatus === 'failed' ? 'text-red-600' : q.embeddingStatus === 'pending' ? 'text-yellow-600' : ''}`}>
                                    {q.embeddingStatus?.toUpperCase()}
                                </div>
                                <ChevronDown
                                    className={`w-6 h-6 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-4 ${q.open ? 'rotate-180' : ''
                                        }`}
                                />
                            </div>

                            {q.open && (
                                <div className="border-t border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50 p-5">
                                    <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
                                        <p className="text-sm font-medium text-gray-500 mb-2">Answer:</p>
                                        <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">
                                            {q.answer || <span className="text-gray-400 italic">No answer provided</span>}
                                        </p>
                                    </div>
                                    <div className="flex justify-end gap-2">
                                        <Button tone="custom"
                                            onClick={() => onEdit(q.id)}
                                            className="flex items-center gap-2 px-4 py-2 text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors font-medium"
                                            title="Edit"
                                        >
                                            <Edit2 className="w-4 h-4" />
                                            Edit
                                        </Button>
                                        <Button tone="custom"
                                            onClick={() => confirmDelete(q.id)}
                                            className="flex items-center gap-2 px-4 py-2 text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors font-medium"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Surface>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default QandATab;
